import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Loader from "../ui/Loader";
import CustomDemoSelect from "../Models/CustomDemoSelect";
import { Download, RotateCcw } from "lucide-react";
import Pagination from "../Models/Pagination";
import * as XLSX from "xlsx";

export const CenterAttendance = () => {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [, setAvailableDates] = useState([]);
  const [selectedDemoId, setSelectedDemoId] = useState("");
  const [centerState, setCenterState] = useState("");

  const API_URL = process.env.REACT_APP_API_URL;

  const indianStates = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
    "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
    "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
    "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
    "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
  ];

  const fetchCenters = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        page,
        limit,
        demoId: selectedDemoId || undefined,
        keyword: search || undefined,
        startDate: startDate ? new Date(`${startDate}T00:00:00`).toISOString() : undefined,
        endDate: endDate ? new Date(`${endDate}T23:59:59`).toISOString() : undefined,
        centerState: centerState || undefined,
      };

      const res = await axios.get(`${API_URL}centerDemoAttendance`, { params });

      if (res.data?.success) {
        const data = res.data.data || [];

        setCenters(data);
        setTotalPages(res.data.totalPages || 1);

        const dates = [
          ...new Set(
            data.map(
              (c) => new Date(c.createdAt).toISOString().split("T")[0]
            )
          ),
        ];
        setAvailableDates(dates);
      } else {
        setCenters([]);
        setAvailableDates([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching center attendance:", error);
      setCenters([]);
      setAvailableDates([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [API_URL, page, limit, selectedDemoId, startDate, endDate, search, centerState]);

  useEffect(() => {
    fetchCenters();
  }, [fetchCenters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCenters();
  };

  const handleDemoChange = (e) => {
    const demoId = e.target.value;
    setSelectedDemoId(demoId);
    setPage(1);
  };

  const onPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      setLoading(true);
    }
  };

  const handleReset = () => {
    setSearch("");
    setStartDate("");
    setEndDate("");
    setSelectedDemoId("");
    setCenterState("");
    setPage(1);
    setLoading(true);
    fetchCenters();
  };

  const exportFields = [
    { key: "centerName", label: "Center Name" },
    { key: "name", label: "Candidate Name" },
    { key: "centerLocation", label: "Location" },
    { key: "centerMobile", label: "Mobile" },
    { key: "demoName", label: "Demo Name" },
    { key: "centerState", label: "State" },
    { key: "createdAt", label: "Date" },
  ];

  const handleExportExcel = () => {
    const excelData = centers.map((center) => {
      let row = {};
      exportFields.forEach((field) => {
        if (field.key === "createdAt") {
          row[field.label] = new Date(center.createdAt).toLocaleString("en-IN", {
            hour12: true,
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          });
        } else {
          row[field.label] = center[field.key] || "";
        }
      });
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    XLSX.writeFile(wb, "CenterAttendance.xlsx");
  };

  return (
    <div className="p-6 rounded-lg shadow-sm border border-gray-100 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-xl font-semibold text-gray-800">
          Center Attendance List
        </div>
        <button
          type="button"
          onClick={handleExportExcel}
          className="bg-[#2C4E86] text-white px-3 py-2 rounded-md text-sm flex items-center gap-2"
        >
          <Download size={16} />
          <span>Export Excel</span>
        </button>
      </div>

      <form onSubmit={handleSearch} className="border-b pb-4">
        <div className="flex items-end gap-3 flex-nowrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by Demo Name..."
              className="mt-1 border rounded-md px-3 py-2 text-sm w-52"
            />
          </div>

          <div className="min-w-[80px]">
            <label className="block text-sm font-medium text-gray-700">Limit</label>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1); // reset page because limit changed
              }}
              className="mt-1 border rounded-md px-3 py-2 text-sm w-full"
            >
              {[10, 20, 50, 100].map((val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
            </select>
          </div>

          {/* State Dropdown */}
          <div className="min-w-[150px]">
            <label className="block text-sm font-medium text-gray-700">State</label>
            <select
              value={centerState}
              onChange={(e) => {
                setCenterState(e.target.value);
                setPage(1);
              }}
              className="mt-1 border rounded-md px-3 py-2 text-sm w-full"
            >
              <option value="">All States</option>
              {indianStates.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-[150px]">
            <CustomDemoSelect selectedDemoId={selectedDemoId} onDemoChange={handleDemoChange} />
          </div>

          <div className="min-w-[140px]">
            <label className="block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 border rounded-md px-3 py-2 text-sm w-full"
            />
          </div>

          <div className="min-w-[140px]">
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1 border rounded-md px-3 py-2 text-sm w-full"
            />
          </div>

          <button
            type="button"
            onClick={handleReset}
            className="bg-gray-200 flex items-center gap-2 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-md text-sm h-[38px] mb-[1px]"
          >
            <RotateCcw size={17} />
            <span className="text-xs">Reset</span>
          </button>
        </div>
      </form>


      {loading ? (
        <Loader />
      ) : centers.length === 0 ? (
        <div className="text-center py-10 text-gray-500">No records found.</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Sno.
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    Center Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    Candidate Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    Mobile
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    Demo Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    State
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {centers.map((center, index) => (
                  <tr key={center._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {(page - 1) * limit + index + 1}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{center.centerName}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{center.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{center.centerLocation}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{center.centerMobile}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{center.demoName}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{center.centerState}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(center.createdAt).toLocaleString("en-IN", {
                        hour12: true,
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} maxVisible={3} />
        </>
      )}
    </div>
  );
};
